import { useDesktopMode } from "@/stores/desktop-mode-store";
import { cn } from "@/lib/cn";
import type { LayoutContextValue } from "@/components/layout/layout-provider";
import type { MasterLayoutData } from "@/components/layout/use-master-layout-data";
import { DesktopRailUtilities } from "@/components/layout/desktop-rail-utilities";
import { useInteractiveLayer } from "@/components/layout/interactive-layer";
import { getCanvasLeftRailLeft } from "@/components/layout/master-side-chrome-position";
import { CanvasLeftRail } from "@/components/canvas/canvas-left-rail";
import { CanvasTimePageMap } from "@/components/canvas/canvas-time-page-map";
import { TaskPocketDock } from "@/components/panels/task-pocket-dock";
import { usePresentationStore } from "@/stores/presentation-store";
import { useWorkspaceViewStore } from "@/stores/workspace-view-store";

interface MasterSideChromeProps {
  data: MasterLayoutData;
  layout: LayoutContextValue;
  isDesktopTauri: boolean;
  showAppHeaderChrome: boolean;
  showCanvasLeftRail: boolean;
  onFitView: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onRailPanMode: () => void;
  panModeActive: boolean;
  panModeLabel?: string;
  focusActive?: boolean;
  onNotePlacement?: () => void;
  notePlacementActive: boolean;
  onTerminalPlacement?: () => void;
  onTaskboardPlacement?: () => void;
  onMemoryPlacement?: () => void;
  onOpenSearch: () => void;
  onOpenNotifications: () => void;
}

export function MasterSideChrome({
  data,
  layout,
  isDesktopTauri,
  showAppHeaderChrome,
  showCanvasLeftRail,
  onFitView,
  onZoomIn,
  onZoomOut,
  onRailPanMode,
  panModeActive,
  panModeLabel,
  focusActive,
  onNotePlacement,
  notePlacementActive,
  onTerminalPlacement,
  onTaskboardPlacement,
  onMemoryPlacement,
  onOpenSearch,
  onOpenNotifications,
}: MasterSideChromeProps) {
  const workspaceVisible = useDesktopMode((state) => state.mode === "denk");
  const focusPresentationActive = usePresentationStore(
    (state) => state.presentation?.variant === "focus",
  );
  // The wall has no left rail: its header holds the project, search, add,
  // theme and close controls.
  const onWall = useWorkspaceViewStore((state) => state.preferred === "wall");
  const showEffectiveCanvasLeftRail =
    showCanvasLeftRail && !focusPresentationActive && !onWall;
  const canvasLeftRailLayer = useInteractiveLayer("canvas-left-rail", {
    active: showEffectiveCanvasLeftRail,
    baseZIndex: 30,
  });
  const railUtilities = (
    <DesktopRailUtilities
      onSearch={onOpenSearch}
      onOpenNotifications={onOpenNotifications}
      onCloseWorkspace={
        isDesktopTauri
          ? () => useDesktopMode.getState().closeWorkspace()
          : undefined
      }
      railVisible={showEffectiveCanvasLeftRail}
      onManageProjects={
        isDesktopTauri
          ? (project) =>
              layout.openSettings(
                project
                  ? {
                      page: "projects-project",
                      args: { projectId: project.id, title: project.name },
                    }
                  : { page: "projects" },
              )
          : undefined
      }
    />
  );
  return (
    <>
      <TaskPocketDock
        projectId={data.currentProject?.id ?? null}
        visible={!isDesktopTauri || workspaceVisible}
        headerChrome={showAppHeaderChrome}
      />

      {/* Focus presentations belong to Compact, so they render neither the
       * canvas rail nor a left-edge reveal handle. */}
      <div
        className={cn(
          "pointer-events-none absolute bottom-20 z-30 flex flex-col justify-center transition-all duration-200 ease-out",
          isDesktopTauri ? "top-4" : "top-11",
          showEffectiveCanvasLeftRail ? "opacity-100" : "opacity-0",
        )}
        style={{
          left: getCanvasLeftRailLeft(),
          ...canvasLeftRailLayer.style,
        }}
        {...canvasLeftRailLayer.interactionProps}
        data-testid="overlay-canvas-left-rail"
      >
        {/* Flex + max-h-full give the rail a real height cap (percentage
         *  max-height resolves against this definite-height flex column), so
         *  the ruler strip inside can measure its squeezed space and
         *  compress its ticks to fit instead of overflowing the work area. */}
        <div
          className={cn(
            "flex max-h-full min-h-0 transition-[opacity,transform] duration-200 ease-out",
            showEffectiveCanvasLeftRail
              ? "pointer-events-auto"
              : "pointer-events-none",
          )}
          data-interactive={showEffectiveCanvasLeftRail ? "true" : undefined}
          data-testid="focus-chrome-left-region"
        >
          {showEffectiveCanvasLeftRail ? (
            <CanvasLeftRail
              topControls={railUtilities}
              onFitView={onFitView}
              onZoomIn={onZoomIn}
              onZoomOut={onZoomOut}
              onPanMode={onRailPanMode}
              panModeActive={panModeActive}
              panModeLabel={panModeLabel}
              focusActive={focusActive}
              onNotePlacement={onNotePlacement}
              notePlacementActive={notePlacementActive}
              onTerminalPlacement={onTerminalPlacement}
              onTaskboardPlacement={onTaskboardPlacement}
              onMemoryPlacement={onMemoryPlacement}
              className="max-h-full"
            />
          ) : null}
        </div>
      </div>

      {showEffectiveCanvasLeftRail ? <CanvasTimePageMap /> : null}
    </>
  );
}

export type { MasterSideChromeProps };
